import { fetchWithAuth } from './secureMedia';

const PORTRAIT_A4 = { width: 595.28, height: 841.89 };
const LANDSCAPE_A4 = { width: 841.89, height: 595.28 };
const PAGE_MARGIN = 32;
const MAX_IMAGE_SIDE = 1800;

let pdfLibPromise = null;

function loadPdfLib() {
  if (window.PDFLib) {
    return Promise.resolve(window.PDFLib);
  }

  if (!pdfLibPromise) {
    pdfLibPromise = new Promise((resolve, reject) => {
      const existingScript = document.getElementById('pdf-lib-vendor-script');
      if (existingScript) {
        existingScript.addEventListener('load', () => resolve(window.PDFLib));
        existingScript.addEventListener('error', reject);
        return;
      }

      const script = document.createElement('script');
      const publicUrl = String(process.env.PUBLIC_URL || '').replace(/\/+$/, '');
      script.id = 'pdf-lib-vendor-script';
      script.src = `${publicUrl}/vendor/pdf-lib.min.js`;
      script.async = true;
      script.onload = () => {
        if (window.PDFLib) {
          resolve(window.PDFLib);
          return;
        }

        reject(new Error('PDF_LIB_NOT_AVAILABLE'));
      };
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  return pdfLibPromise;
}

function stripExtension(value) {
  const raw = String(value || '').trim().split(/[\\/]/).pop() || '';
  return raw.replace(/\.[a-z0-9]+$/i, '');
}

export function buildCombinedEvidencePdfName(fileName, fallbackBaseName) {
  const baseName =
    stripExtension(fileName) || stripExtension(fallbackBaseName) || 'evidencias';
  return `Inf${baseName.slice(0, 80)}.pdf`;
}

function dataUrlToBlob(dataUrl) {
  return fetch(dataUrl).then(response => response.blob());
}

async function resolveEvidenceBlob(evidence) {
  if (evidence?.file instanceof Blob) return evidence.file;
  if (evidence?.blob instanceof Blob) return evidence.blob;

  const base64 = String(evidence?.base64 || '');
  if (base64.startsWith('data:')) {
    return dataUrlToBlob(base64);
  }

  const sourceUrl = evidence?.sourceUrl;
  if (!sourceUrl) return null;

  const response = await fetchWithAuth(sourceUrl, { credentials: 'include' });
  if (!response.ok) {
    throw new Error(`EVIDENCE_DOWNLOAD_FAILED_${response.status}`);
  }

  return response.blob();
}

function isPdfEvidence(evidence, blob) {
  const type = String(evidence?.fileType || blob?.type || '').toLowerCase();
  const name = String(evidence?.fileName || evidence?.sourceUrl || '').toLowerCase();
  return type === 'pdf' || type === 'application/pdf' || name.includes('.pdf');
}

function getImageSize(imageLike) {
  return {
    width: imageLike?.width || imageLike?.naturalWidth || 0,
    height: imageLike?.height || imageLike?.naturalHeight || 0
  };
}

function loadHtmlImage(blob) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = error => {
      URL.revokeObjectURL(url);
      reject(error);
    };
    image.src = url;
  });
}

async function loadBrowserImage(blob) {
  if (typeof createImageBitmap === 'function') {
    try {
      return createImageBitmap(blob, { imageOrientation: 'from-image' });
    } catch {
      // Fallback for browsers or file types that do not support createImageBitmap.
    }
  }

  return loadHtmlImage(blob);
}

async function imageBlobToJpegBytes(blob) {
  const image = await loadBrowserImage(blob);
  const { width, height } = getImageSize(image);

  if (!width || !height) {
    throw new Error('INVALID_IMAGE_SIZE');
  }

  const scale = Math.min(1, MAX_IMAGE_SIDE / Math.max(width, height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));

  const context = canvas.getContext('2d');
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.drawImage(image, 0, 0, canvas.width, canvas.height);

  if (typeof image.close === 'function') {
    image.close();
  }

  const jpegBlob = await new Promise(resolve =>
    canvas.toBlob(resolve, 'image/jpeg', 0.92)
  );

  if (!jpegBlob) {
    throw new Error('IMAGE_CONVERSION_FAILED');
  }

  return jpegBlob.arrayBuffer();
}

async function appendPdfPages(pdfDoc, blob, PDFDocument) {
  const sourcePdf = await PDFDocument.load(await blob.arrayBuffer(), {
    ignoreEncryption: true
  });
  const copiedPages = await pdfDoc.copyPages(
    sourcePdf,
    sourcePdf.getPageIndices()
  );
  copiedPages.forEach(page => pdfDoc.addPage(page));
}

async function appendImagePage(pdfDoc, blob) {
  const jpegBytes = await imageBlobToJpegBytes(blob);
  const embeddedImage = await pdfDoc.embedJpg(jpegBytes);
  const isLandscape = embeddedImage.width > embeddedImage.height;
  const pageSize = isLandscape ? LANDSCAPE_A4 : PORTRAIT_A4;
  const page = pdfDoc.addPage([pageSize.width, pageSize.height]);
  const maxWidth = pageSize.width - PAGE_MARGIN * 2;
  const maxHeight = pageSize.height - PAGE_MARGIN * 2;
  const scale = Math.min(
    maxWidth / embeddedImage.width,
    maxHeight / embeddedImage.height
  );
  const imageWidth = embeddedImage.width * scale;
  const imageHeight = embeddedImage.height * scale;

  page.drawImage(embeddedImage, {
    x: (pageSize.width - imageWidth) / 2,
    y: (pageSize.height - imageHeight) / 2,
    width: imageWidth,
    height: imageHeight
  });
}

export async function buildCombinedEvidencePdfBlob(evidences) {
  const { PDFDocument } = await loadPdfLib();
  const pdfDoc = await PDFDocument.create();

  for (const evidence of evidences || []) {
    const blob = await resolveEvidenceBlob(evidence);
    if (!blob) continue;

    if (isPdfEvidence(evidence, blob)) {
      await appendPdfPages(pdfDoc, blob, PDFDocument);
    } else {
      await appendImagePage(pdfDoc, blob);
    }
  }

  if (pdfDoc.getPageCount() === 0) {
    throw new Error('NO_EVIDENCE_PAGES');
  }

  const bytes = await pdfDoc.save();
  return new Blob([bytes], { type: 'application/pdf' });
}
